import { useMemo } from 'react';
import DescriptionIcon from '@material-ui/icons/Description';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import CachedIcon from '@material-ui/icons/Cached';
import { ActionButton, ActionButtonProps } from './ActionButton';
import { EntityRefreshData, EntityUrls } from '../hooks';
import { useApi, useRouteRef } from '@backstage/core-plugin-api';
import {
  getEntitySourceLocation,
  useEntity,
} from '@backstage/plugin-catalog-react';

import {
  ScmIntegrationIcon,
  scmIntegrationsApiRef,
} from '@backstage/integration-react';
import MenuBookIcon from '@material-ui/icons/MenuBook';
import { catalogPlugin } from '@backstage/plugin-catalog';

export interface ActionButtonsListProps {
  urls: EntityUrls;
  refreshData: EntityRefreshData;
}

export const ActionButtonsList: React.FC<ActionButtonsListProps> = ({
  urls,
  refreshData,
}) => {
  const { entity } = useEntity();
  const scmIntegrationsApi = useApi(scmIntegrationsApiRef);
  const techdocsRouteRef = useRouteRef(
    catalogPlugin.externalRoutes.viewTechDoc,
  );

  // Type assertion needed due to duplicate @backstage/integration packages in monorepo
  const entitySourceLocation = getEntitySourceLocation(
    entity,
    scmIntegrationsApi as any,
  );

  const buttons = useMemo(() => {
    // Construct proper TechDocs URL
    const hasTechdocs = urls.techdocsLocation || urls.techdocsExternalLocation;
    const techdocsUrl =
      hasTechdocs && techdocsRouteRef
        ? techdocsRouteRef({
            namespace: entity.metadata.namespace || 'default',
            kind: entity.kind.toLowerCase(),
            name: entity.metadata.name,
          })
        : undefined;

    // Extract repository URL from source location
    const repositoryUrl =
      entitySourceLocation?.integrationType === 'github'
        ? entitySourceLocation.locationTargetUrl?.replace(/\/blob\/.*$/, '')
        : entitySourceLocation?.locationTargetUrl;

    const buttonConfigs: ActionButtonProps[] = [
      {
        tooltip: 'Refresh',
        icon: CachedIcon,
        ariaLabel: 'Refresh',
        onClick: refreshData.refreshEntity,
        condition: refreshData.allowRefresh && refreshData.canRefresh,
      },
      {
        url: urls.editUrl,
        tooltip: 'Edit Definition File',
        icon: DescriptionIcon,
        ariaLabel: 'Edit Definition File',
        condition: !!urls.editUrl,
      },
      {
        url: repositoryUrl,
        tooltip: 'View Source',
        icon: entitySourceLocation?.integrationType
          ? ScmIntegrationIcon
          : OpenInNewIcon,
        ariaLabel: 'View Source',
        condition:
          !!repositoryUrl &&
          !repositoryUrl.includes('github.com/placeholder/unknown-repository'),
      },
      {
        url: techdocsUrl,
        tooltip: 'View TechDocs',
        icon: MenuBookIcon,
        ariaLabel: 'View TechDocs',
        condition: !!hasTechdocs,
      },
    ];

    return buttonConfigs.filter(button => button.condition !== false);
  }, [urls, refreshData, entity, entitySourceLocation, techdocsRouteRef]);

  return (
    <>
      {buttons.map((button, index) => (
        <ActionButton key={button.tooltip || `action-${index}`} {...button} />
      ))}
    </>
  );
};
